import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

/**
 * Convite e lotação para administrador e responsável pela unidade (migration
 * 308). Quem decide o que cada um pode é o banco (papeis_que_posso_dar,
 * pode_gerir_usuario, unidade_do_convite); aqui só a chamada.
 */

/** Funções que contam como gestão: podem ser gerente de alguém. */
export const PAPEIS_GESTAO = ["admin_organizacao", "gerente_unidade", "manager"];

export interface PessoaDaLista {
  id: string;
  email: string;
  is_super_admin: boolean;
  unidade_id?: string | null;
  products: Array<{ role_code: string; role_name: string }>;
}

export interface Gestor {
  crmId: number;
  nome: string;
  unidadeId: string | null;
}

export const papelDe = (p: PessoaDaLista): string =>
  p.is_super_admin ? "super_admin" : p.products[0]?.role_code ?? "user";

/** Gestores da lista (já recortada pelo banco), com o id de `users`. */
export async function gestoresDaLista(pessoas: PessoaDaLista[]): Promise<Gestor[]> {
  const ids = pessoas.filter((p) => PAPEIS_GESTAO.includes(papelDe(p))).map((p) => p.id);
  if (ids.length === 0) return [];
  const { data } = await supabase
    .from("users")
    .select("id, user_id, name, unidade_id" as never)
    .in("user_id", ids)
    .order("name");
  return ((data ?? []) as unknown as Array<{ id: number; name: string | null; unidade_id: string | null }>)
    .filter((u) => u.name)
    .map((u) => ({ crmId: u.id, nome: u.name as string, unidadeId: u.unidade_id }));
}

export async function idDaPessoa(authUserId: string): Promise<{ crmId: number; managerId: number | null; unidadeId: string | null } | null> {
  const { data } = await supabase
    .from("users")
    .select("id, manager_id, unidade_id" as never)
    .eq("user_id", authUserId)
    .maybeSingle();
  const linha = data as unknown as { id: number; manager_id: number | null; unidade_id: string | null } | null;
  return linha ? { crmId: linha.id, managerId: linha.manager_id, unidadeId: linha.unidade_id } : null;
}

export async function convidarPessoa(dados: {
  email: string;
  nome: string;
  cpfCnpj: string;
  roleId: string;
  unidadeId: string | null;
}): Promise<void> {
  const { data: sessao } = await supabase.auth.getSession();
  if (!sessao.session) throw new Error("Sessão expirada. Entre novamente.");
  const resposta = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessao.session.access_token}`,
    },
    body: JSON.stringify({
      email: dados.email.trim().toLowerCase(),
      name: dados.nome.trim(),
      cpfCnpj: dados.cpfCnpj,
      roleId: dados.roleId,
      isSuperAdmin: false,
      unidadeId: dados.unidadeId,
    }),
  });
  if (!resposta.ok) {
    const corpo = await resposta.json().catch(() => null);
    throw new Error(corpo?.error || "Não foi possível enviar o convite.");
  }
}

/** Id em `users` de quem acabou de ser convidado (o convite grava pelo e-mail). */
export async function idPeloEmail(email: string): Promise<number | null> {
  const { data } = await supabase.from("users").select("id").eq("email", email.trim().toLowerCase()).maybeSingle();
  return (data as { id: number } | null)?.id ?? null;
}
